// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { NewsUpdate } from '../../types/admin';
import { format } from 'date-fns';
import { RichTextEditor } from './RichTextEditor';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { useAdminToast } from './useAdminToast';

interface AdminNewsProps {
  session: Session;
  profile: Profile | null;
  showCreateForm?: boolean;
}

export const AdminNews: React.FC<AdminNewsProps> = ({ session, profile, showCreateForm = false }) => {
  const [news, setNews] = useState<NewsUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(showCreateForm);
  const [editingNews, setEditingNews] = useState<NewsUpdate | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const { showSuccess, showError } = useAdminToast();
  const [formData, setFormData] = useState({
    type: 'features' as 'features' | 'improvements' | 'announcements',
    title: '',
    description: '',
    icon: '📢',
    version: '',
    date: format(new Date(), 'MMMM yyyy'),
    highlights: [] as string[],
    cta_text: '',
    cta_action: '',
    is_published: true,
  });

  const normalizeHighlights = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            if ('text' in item && typeof item.text === 'string') return item.text;
            if ('title' in item && typeof item.title === 'string') return item.title;
          }
          return '';
        })
        .filter((item) => item.trim() !== '');
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return normalizeHighlights(parsed);
        }
      } catch {
        return value
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean);
      }
    }

    return [];
  };

  useEffect(() => {
    loadNews();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-news-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'news_updates',
        },
        () => {
          loadNews();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadNews = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('news_updates')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) {
        if (error.message?.includes('does not exist') || error.code === '42P01') {
          setNews([]);
          return;
        }
        throw error;
      }
      setNews(data || []);
    } catch (err) {
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newsData = {
        ...formData,
        highlights: formData.highlights.filter((h) => h.trim() !== ''),
      };

      if (editingNews) {
        const { error } = await supabase
          .from('news_updates')
          .update(newsData)
          .eq('id', editingNews.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('news_updates').insert(newsData);
        if (error) throw error;
      }
      setShowForm(false);
      setEditingNews(null);
      resetForm();
      await loadNews();
      showSuccess(editingNews ? 'News update updated successfully' : 'News update created successfully');
    } catch (err: any) {
      console.error('Error saving news:', err);
      showError(err.message || 'Failed to save news update');
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteTarget(id);
    setShowDeleteDialog(true);
  };

  const handleEdit = (item: NewsUpdate) => {
    setShowForm(true);
    setEditingNews(item);
    setFormData({
      type: item.type || 'features',
      title: item.title || '',
      description: item.description || '',
      icon: item.icon || '📢',
      version: item.version || '',
      date: item.date || format(new Date(), 'MMMM yyyy'),
      highlights: normalizeHighlights(item.highlights),
      cta_text: item.cta_text || '',
      cta_action: item.cta_action || '',
      is_published: item.is_published ?? true,
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const { error } = await supabase.from('news_updates').delete().eq('id', deleteTarget);
      if (error) throw error;
      await loadNews();
      showSuccess('News update deleted successfully');
    } catch (err: any) {
      console.error('Error deleting news:', err);
      showError(err.message || 'Failed to delete news update');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'features',
      title: '',
      description: '',
      icon: '📢',
      version: '',
      date: format(new Date(), 'MMMM yyyy'),
      highlights: [],
      cta_text: '',
      cta_action: '',
      is_published: true,
    });
  };

  const addHighlight = () => {
    setFormData((prev) => ({
      ...prev,
      highlights: [...prev.highlights, ''],
    }));
  };

  const updateHighlight = (index: number, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.highlights];
      updated[index] = value;
      return { ...prev, highlights: updated };
    });
  };

  const removeHighlight = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      highlights: prev.highlights.filter((_, idx) => idx !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            News & Updates Management
          </h1>
          <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
            Manage product news and feature announcements
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingNews(null);
            resetForm();
          }}
          className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 active:bg-luna-secondary/80 transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation"
        >
          Create News Update
        </button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
            {editingNews ? 'Edit News Update' : 'Create News Update'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="news-type" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Type
                </label>
                <select
                  id="news-type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as 'features' | 'improvements' | 'announcements',
                    })
                  }
                  className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-base sm:text-sm min-h-[44px] sm:min-h-[40px] touch-manipulation"
                >
                  <option value="features">Features</option>
                  <option value="improvements">Improvements</option>
                  <option value="announcements">Announcements</option>
                </select>
              </div>
              <div>
                <label htmlFor="news-icon" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Icon
                </label>
                <input
                  id="news-icon"
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-base sm:text-sm min-h-[44px] sm:min-h-[40px]"
                />
              </div>
            </div>
            <div>
              <label htmlFor="news-title" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Title
              </label>
              <input
                id="news-title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 sm:py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-base sm:text-sm min-h-[44px] sm:min-h-[40px]"
              />
            </div>
            <div>
              <label htmlFor="news-description" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Description
              </label>
              <RichTextEditor
                id="news-description"
                content={formData.description}
                onChange={(content) => setFormData({ ...formData, description: content })}
                placeholder="Enter news description..."
              />
            </div>
            <div>
              <label htmlFor="news-highlights" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                Highlights
              </label>
              <div id="news-highlights" className="space-y-2">
                {formData.highlights.map((highlight, index) => (
                  <div key={`highlight-${index}`} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={highlight}
                      onChange={(e) => updateHighlight(index, e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-sm text-luna-text-light dark:text-luna-text-dark"
                      placeholder={`Highlight ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeHighlight(index)}
                      className="px-2 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 active:bg-red-700 text-sm"
                      aria-label="Remove highlight"
                    >
                      <span className="material-symbols-outlined text-base">delete</span>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHighlight}
                  className="px-3 py-2 bg-luna-secondary/10 text-luna-secondary border border-luna-secondary/30 rounded-lg text-sm font-semibold hover:bg-luna-secondary/20"
                >
                  + Add highlight
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="w-5 h-5 sm:w-4 sm:h-4 rounded border-luna-secondary/30 touch-manipulation"
              />
              <label htmlFor="is_published" className="text-sm text-luna-text-light dark:text-luna-text-dark">
                Published
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 active:bg-luna-secondary/80 transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation"
              >
                {editingNews ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingNews(null);
                  resetForm();
                }}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 active:bg-gray-700 transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0 overscroll-x-contain touch-pan-x">
          <div className="inline-block min-w-full px-4 sm:px-0">
            <table className="w-full min-w-[500px] sm:min-w-0">
              <thead className="bg-luna-secondary/10">
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                    Title
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                    Type
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                    Status
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {news.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-luna-secondary/10 hover:bg-luna-secondary/5 active:bg-luna-secondary/10"
                  >
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {item.icon} {item.title}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {item.type}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          item.is_published
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                        }`}
                      >
                        {item.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                      <button
                        onClick={() => handleEdit(item)}
                        className="px-3 py-2 sm:py-1 bg-blue-500 text-white rounded hover:bg-blue-600 active:bg-blue-700 text-xs sm:text-xs min-h-[44px] sm:min-h-[32px] touch-manipulation mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-3 py-2 sm:py-1 bg-red-500 text-white rounded hover:bg-red-600 active:bg-red-700 text-xs sm:text-xs min-h-[44px] sm:min-h-[32px] touch-manipulation"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete News Update"
        message="Are you sure you want to delete this news update? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setDeleteTarget(null);
        }}
        variant="danger"
      />
    </div>
  );
};
